/**
 * SOLID Review
 * S — Acceptable. Two responsibilities live here: `AuthRouter` (a lookup-table mapping `authStore.screen`
 *     to a lazy-loaded screen component, plus the one-time `?join=`/`?payment=` URL-bootstrap effect) and
 *     `App` (wiring ErrorBoundary, MaintenanceBanner, CookieConsentProvider and Suspense around it). Both
 *     are "own the top-level shell", so it reads as cohesive rather than mixed.
 * O — Good. A new non-GAME screen is a new `SCREEN_RENDERERS` table entry, not an edited
 *     branch — `AuthRouter`'s only remaining `if` is the GAME screen's genuinely different
 *     wrapping (`ComponentFrameworkProvider` instead of `Suspense`+`LoadingBoundary`), which
 *     a table entry can't express uniformly anyway.
 * L — N/A — no inheritance hierarchy.
 * I — Good. `App` takes no props; `AuthRouter` reads the screen from the client view and calls one use
 *     case, the session's `startSession` (strict-ports FE2; since FE3 it reads the token's account too).
 *     Narrow, cohesive.
 * D — Depends on the boundary components, `MaintenanceBanner`, `CookieConsentProvider`
 *     and the client view, plus lazy imports of 6 screen components — all same-layer
 *     framework/application concerns, no direct infrastructure imports. Good. Now also
 *     wraps every screen in `ComponentFrameworkProvider`, handing it the composition root's
 *     ASSEMBLED Framework component and its host resources (strict-ports FE6a: until then
 *     the provider connected a fresh component from an adapter registry of Apollo, REST,
 *     LiveKit and health adapters, all moved behind the Application's use cases in FE1–FE5). It
 *     is NOT a composition root since strict-ports FE7: it wired the Apollo slow-request banner to
 *     `useUIStore` until then, which named an Infrastructure module; `frontendRoot` does that now. (It
 *     also wired the auth store's post-logout reset until FE3, when signing out became a decision whose
 *     changes reset the table.)
 */
import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from './boundaries/ErrorBoundary';
import { LoadingBoundary } from './boundaries/LoadingBoundary';
import { MaintenanceBanner } from './components/layout/MaintenanceBanner';
import { CookieConsentProvider } from './consent/CookieConsentProvider';
import type { IEntitySessionView } from '../application/ports/ClientStateView';
import { useClientView, useClientAction } from './client/useClientPorts';
import type { TClientUseCaseInput } from '../application/ports/ClientUseCases.contract';
import { ComponentFrameworkProvider } from './uml-components/ComponentFramework';
import { frontendGraph, frameworkHost, checkServerHealth } from './composition/frontendRoot';

// Eager: Apollo is already loaded by AppRoot.tsx itself (see the lazy-import comment
// below), so routing its operations through a ComponentFramework adapter carries no
// extra bundle-size cost — unlike LiveKit's real-time client factory (liveKitClientFactory,
// below), this can be registered up front.

// The slow-request banner's wiring (apolloClient → the UI store) is the composition root's since strict-ports
// FE7: it names a concrete Infrastructure module, which only `framework/composition/` may
// (`request-adapters-no-infrastructure`). It runs in `frontendRoot`, at the same module load.

// The cookie-consent CMP is no longer wired here (strict-ports FE4b): `configureConsent` handed the
// provider a gateway and an audit service built at module load. The gateway is a host resource of
// Infrastructure now (`BrowserPlatform`), the audit record is `ClientLegalServer`'s, and the provider
// talks to the Application's use cases like every other component.

// Lazy: AppRoot statically imports Apollo (~179 kB) which is only needed in the GAME screen.
// Deferring it prevents that chunk from being parsed on the WelcomeScreen / auth screens.
const AppRoot = React.lazy(() =>
  import('./components/AppRoot').then((m) => ({ default: m.AppRoot }))
);

const WelcomeScreen = React.lazy(() => import('./components/auth/WelcomeScreen'));
const EnterCodeScreen = React.lazy(() => import('./components/auth/EnterCodeScreen'));
const GuestChoiceScreen = React.lazy(() => import('./components/auth/GuestChoiceScreen'));
const VerifyCodeScreen = React.lazy(() => import('./components/auth/VerifyCodeScreen'));
const LoginScreen = React.lazy(() => import('./components/auth/LoginScreen'));
const PaymentSuccessScreen = React.lazy(() => import('./components/payment/PaymentSuccessScreen'));

// Every screen except GAME (handled separately below — it needs ComponentFrameworkProvider,
// not Suspense+LoadingBoundary) and WELCOME (the fallback default) renders via this table.
// Adding a new screen is a new entry here, not a new branch in AuthRouter.
const SCREEN_RENDERERS: Partial<Record<IEntitySessionView['screen'], () => React.ReactElement>> = {
  PAYMENT_SUCCESS: () => <PaymentSuccessScreen />,
  ENTER_CODE: () => <EnterCodeScreen />,
  GUEST_CHOICE: () => <GuestChoiceScreen />,
  VERIFY_EMAIL: () => <VerifyCodeScreen purpose="VERIFY_EMAIL" />,
  LOGIN: () => <LoginScreen />,
  VERIFY_LOGIN: () => <VerifyCodeScreen purpose="LOGIN" />,
};

// React 18 StrictMode (dev mode — which this app runs in production via `vite dev`,
// see docker-compose.yml) double-invokes mount effects. The first invocation strips
// `?join=`/`?payment=` from the URL via history.replaceState, so a naive second
// invocation would re-read an empty query string and re-run the session's start, clobbering
// the screen/pendingRoomCode set by the first. Guard with a module-level flag so the
// one-time URL-bootstrap logic below only ever runs once per page load.
let authRouterBootstrapped = false;

function AuthRouter(): React.ReactElement {
  const screen = useClientView((s) => s.session.screen);
  const startSession = useClientAction('startSession');

  useEffect(() => {
    if (authRouterBootstrapped) return;
    authRouterBootstrapped = true;

    // What the URL asked for — Stripe's return, or a `?join=<code>` link (the same smart join as the
    // funnel's code) — read here, where the browser is, and stripped so a reload does not repeat it.
    const params = new URLSearchParams(window.location.search);
    const paymentReturn = params.get('payment') === 'success';
    const joinCode = paymentReturn ? null : params.get('join');
    if (paymentReturn || joinCode) history.replaceState(null, '', window.location.pathname);

    // Who the tab is — the stored token's account (FE3), the stored guest — and where it lands are the
    // session's (strict-ports FE2).
    const intent: TClientUseCaseInput<'startSession'> = { joinCode, paymentReturn };
    void startSession(intent);
  }, [startSession]);

  if (screen === 'GAME') {
    // The LiveKit client FACTORY reaches AppRoot unresolved (a host resource on the Framework's context):
    // creating a client downloads the livekit-client SDK chunk, which happens only when the player opts
    // into video, never on a game-screen visit as such.
    return <AppRoot />;
  }

  const renderScreen = SCREEN_RENDERERS[screen];
  return (
    <Suspense fallback={<LoadingBoundary />}>
      {renderScreen ? renderScreen() : <WelcomeScreen />}
    </Suspense>
  );
}

const App: React.FC = () => (
  <ErrorBoundary>
    <MaintenanceBanner checkHealth={checkServerHealth} />
    {/*
      The provider wraps EVERY screen, not just GAME (fixed 2026-08-17).

      It used to wrap only the GAME branch's <AppRoot />, which was correct while the auth screens
      got their collaborators from module singletons. It stopped being correct the moment those
      screens were repointed at the registry: WelcomeScreen, LoginScreen, RegisterScreen,
      VerifyCodeScreen, EnterCodeScreen and PaymentSuccessScreen all call useComponentFramework()
      now, and all of them render on this side of the tree. The result was a white screen on load —
      "useComponentFramework must be used within <ComponentFrameworkProvider>" out of WelcomeScreen,
      caught by the e2e warmup, invisible to every unit test because those render the component
      inside a provider or mock the hook outright.

      Hoisting costs nothing at load: this module already imports apolloClient and the Apollo
      adapters statically (see the eager-registry note above), and bootstrap makes no network call —
      it resolves apolloClientFactory, which nothing registers. The lazy chunks stay lazy.
    */}
    <ComponentFrameworkProvider framework={frontendGraph.framework} host={frameworkHost}>
      {/* Inside the provider since strict-ports FE4b: it reads the client view and calls use cases. */}
      <CookieConsentProvider />
      <Suspense fallback={<LoadingBoundary />}>
        <AuthRouter />
      </Suspense>
    </ComponentFrameworkProvider>
  </ErrorBoundary>
);

export default App;
